import React, { useRef } from 'react';
import { useEvent } from '@soap/modules';
import { unitsViewStates, viewStates } from '../constants';
import {
    mapE271TopicRowToE179,
    mapE308TenantCurriculumToLibraryShape,
    parseLibraryE271MergeTarget,
    parseStudentCurriculumLazyMergeTarget,
    computeStudentCurriculumExpandNodesForPlacements,
    type CurriculumPlacementKey
} from '../utils/curriculumUtils';
import { convertTopic, updateTopicDetails, removeTopicsFromData } from '../utils/topicUtils';
import {
    applyAssessmentApprovedToUnits,
    applyAssessmentCancelledToUnits,
    applyAssessmentCompletedToUnits,
    applyAssessmentResultsDeletedToUnits,
    applyAssessmentSharedToUnits,
    applyAssessmentStartedToUnits,
    applySharedAssessmentRevokedToUnits,
    applyUnitTouchedToUnits
} from '../utils/unitAssessmentPatches';
import {
    applyLessonPatchToUnits,
    applyLessonRasterFigureApprovedToUnits,
    applyLessonRasterFigureReadyToUnits,
    applyLessonRemovedFromUnits,
    applyLessonVideoApprovedToUnits,
    buildPatchFromE258,
    buildPatchFromE261,
    buildPatchFromE318,
    buildPatchFromE326,
    mapEventRasterOptions,
    mapWireInstructionStepsFromEvent,
    mergePreservedVideoApprovals
} from '../utils/unitLessonPatches';
import { replaceUnitsPreservingUnchangedRefs } from '../utils/unitsListReplace';
import { useLessonsPageState } from './useLessonsPageState';
import { useLessonsPageCommands } from './useLessonsPageCommands';

function normalizeStudentIdForCompare(id: string | null | undefined): string {
    if (id == null || id === '') return '';
    return id.toString().toLowerCase().trim().replace(/[{}]/g, '');
}

/** Reject E180/E271 payloads for a different student than currently selected. Missing id: accept (legacy / forward compat). */
function shouldApplyCurriculumEventForStudent(
    eventStudentId: string | null | undefined,
    selectedStudentId: string | null
): boolean {
    if (eventStudentId == null || String(eventStudentId).trim() === '') {
        return true;
    }
    if (!selectedStudentId) {
        return true;
    }
    return normalizeStudentIdForCompare(String(eventStudentId)) === normalizeStudentIdForCompare(selectedStudentId);
}

export function useLessonsPageEvents(
    state: ReturnType<typeof useLessonsPageState>,
    commands: ReturnType<typeof useLessonsPageCommands>,
    selectedStudentId: string | null,
    lessonsPanelRef: React.RefObject<HTMLDivElement>,
    addTopicsWizardBatchingRef?: React.MutableRefObject<boolean>,
    pendingAddTopicsExpandPlacementsRef?: React.MutableRefObject<CurriculumPlacementKey[] | null>,
    onTopicsRemovedFromStudentCurriculum?: () => void,
    createYourOwnWizardActiveRef?: React.MutableRefObject<boolean>
) {
    // Ref to store preserved expansion state when adding custom topics
    const preservedExpansionRef = useRef<{ expandedNodes: Set<string>, loadedNodeIds: Set<string> } | null>(null);
    
    const {
        setBackendLessons,
        setUnitsViewState,
        setCurriculumData,
        setTenantCustomCurriculumLibraryData,
        setViewState,
        setLoadedNodeIds,
        setLoadingNodeIds,
        setLoadingTopicDetails,
        selectedTopics,
        setSelectedTopics,
        setStudentCurriculumData,
        setShowAddTopicsDialog,
        setShowCreateYourOwnWizard,
        setShowCreateCustomTopicsDialog,
        setShowAddCustomTopicDialog,
        setWizardInitialCatalogSubjectId,
        setExpandedCards,
        setIsStudentCurriculumActive,
        setSelectedProvider,
        setSelectedLearningStyles,
        setHasAttemptedSubmit,
        curriculumData,
        studentCurriculumData,
        isStudentCurriculumActiveRef,
        expandedNodes,
        setExpandedNodes,
        loadedNodeIds
    } = state;

    const { sendGetUnitsCommand, consumeGetUnitsResponse, noteUnitsLocalMutation, sendGetStudentCurriculumCommand } =
        commands;

    /**
     * Optimistic units patch + C251v1_GetUnits. Complements AssessmentCard onRefresh for
     * E172/E174/E180/E270 — do not treat card onRefresh as redundant and remove it (Jul 2026
     * regression: live assessment status on the unit card stopped updating).
     *
     * Only bump the GetUnits content epoch when the patch changes state; a no-op bump can
     * discard an in-flight GetUnits that would have fixed the card.
     */
    const patchUnitsThenRefresh = (patcher: (prev: any[]) => any[]) => {
        // React runs this updater synchronously in event handlers.
        let didChange = false;
        setBackendLessons(prev => {
            const next = patcher(prev as any[]) as any;
            didChange = next !== prev;
            return next;
        });
        if (didChange) {
            noteUnitsLocalMutation();
        }
        if (selectedStudentId) {
            sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
        }
    };

    useEvent({
        eventName: 'E251v1_GotUnits',
        onEventReceived: (event) => {
            const { shouldApply } = consumeGetUnitsResponse();
            const units = event?.e251_Units ?? event?.E251_Units;
            if (shouldApply && units) {
                setBackendLessons(prev =>
                    mergePreservedVideoApprovals(
                        prev as any[],
                        replaceUnitsPreservingUnchangedRefs(prev as any[], units as any[]) as any[]
                    ) as any
                );
                setUnitsViewState(unitsViewStates.ready);
            } else if (units) {
                setUnitsViewState(unitsViewStates.ready);
            }
        }
    });

    useEvent({
        eventName: 'E179v1_GotCurriculumStructure',
        onEventReceived: (event) => {
            if (event) {
                // Only set curriculum data if we're viewing a provider (not student curriculum)
                // Use ref to get current value, not stale closure value
                if (!isStudentCurriculumActiveRef.current) {
                    setCurriculumData(event);
                    setViewState(viewStates.curriculumReady);
                    // Reset loaded nodes when curriculum changes
                    setLoadedNodeIds(new Set());
                    setLoadingNodeIds(new Set());
                }
            }
        }
    });

    useEvent({
        eventName: 'E308v1_GotTenantCustomCurriculum',
        onEventReceived: (event: any) => {
            if (event) {
                setTenantCustomCurriculumLibraryData(mapE308TenantCurriculumToLibraryShape(event));
            }
        }
    });

    useEvent({
        eventName: 'E271v1_GotCurriculumNodeChildren',
        onEventReceived: (event) => {
            const eAny = event as Record<string, unknown> | null | undefined;
            const nodeId = (eAny?.e271_NodeId ?? eAny?.E271_NodeId) as string | undefined;
            const nodeType = (eAny?.e271_NodeType ?? eAny?.E271_NodeType) as string | undefined;
            const topics = (eAny?.e271_Topics ?? eAny?.E271_Topics) as any[] | undefined;
            if (!event || !nodeId || !topics) {
                return;
            }
            const e271Student = (event as { e271_StudentId?: string; E271_StudentId?: string }).e271_StudentId
                ?? (event as { E271_StudentId?: string }).E271_StudentId;
            if (!shouldApplyCurriculumEventForStudent(e271Student, selectedStudentId)) {
                setLoadingNodeIds(prev => {
                    const next = new Set(prev);
                    next.delete(nodeId);
                    return next;
                });
                return;
            }
            setLoadingNodeIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(nodeId);
                return newSet;
            });

            setLoadedNodeIds(prev => {
                const newSet = new Set(prev);
                newSet.add(nodeId);
                return newSet;
            });

            setCurriculumData(prevData => {
                if (!prevData || !prevData.e179_Subjects) return prevData;

                const updatedData = JSON.parse(JSON.stringify(prevData)) as typeof prevData;
                const target = parseLibraryE271MergeTarget(updatedData.e179_Subjects as any[], nodeId, nodeType ?? '');
                if (!target) {
                    return updatedData;
                }
                const mappedTopics = topics.map((t: any) => mapE271TopicRowToE179(t));

                if (target.kind === 'year') {
                    const ks = target.subject.e179_KeyStages?.[target.keystageIndex];
                    const yearRow = ks?.e179_Years?.[target.yearIndex];
                    if (yearRow) {
                        yearRow.e179_Topics = mappedTopics;
                    }
                } else {
                    const ks = target.subject.e179_KeyStages?.[target.keystageIndex];
                    const yearRow = ks?.e179_Years?.[target.yearIndex];
                    if (!yearRow?.e179_Topics) {
                        return updatedData;
                    }
                    let topic: any = yearRow.e179_Topics[target.topicIndices[0]];
                    for (let i = 1; i < target.topicIndices.length && topic; i++) {
                        const idx = target.topicIndices[i];
                        if (topic.e179_ChildTopics && topic.e179_ChildTopics[idx] !== undefined) {
                            topic = topic.e179_ChildTopics[idx];
                        } else {
                            topic = null;
                            break;
                        }
                    }
                    if (topic) {
                        topic.e179_ChildTopics = mappedTopics;
                    }
                }

                return updatedData;
            });
        }
    });

    useEvent({
        eventName: 'E272v1_GotTopicDetails',
        onEventReceived: (event) => {
            console.log('Received E272v1_GotTopicDetails event:', event);
            if (event && event.e272_TopicId) {
                const topicId = event.e272_TopicId.toString();
                const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
                const normalizedTopicId = normalizeId(topicId);
                
                // Remove from loading set
                setLoadingTopicDetails(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(topicId);
                    return newSet;
                });
                
                const detailsToAdd = {
                    e179_PriorKnowledgeRequired: event.e272_PriorKnowledgeRequired || [],
                    e179_NationalCurriculumContent: event.e272_NationalCurriculumContent || [],
                    e179_Keywords: event.e272_Keywords?.map((k: any) => ({
                        e179_Keyword: k.e272_Keyword,
                        e179_Description: k.e272_Description
                    })) || [],
                    e179_LearningObjectives: event.e272_LearningObjectives || [],
                    e179_SupervisionLevel: event.e272_SupervisionLevel,
                    e179_Outcome: event.e272_Outcome,
                    e179_TeacherTip: event.e272_TeacherTip,
                    e179_Misconceptions: event.e272_Misconceptions,
                    e179_DetailsLoaded: true
                };
                
                // Update topic details in curriculum data
                updateTopicDetails(
                    topicId,
                    detailsToAdd,
                    curriculumData,
                    setCurriculumData,
                    studentCurriculumData,
                    setStudentCurriculumData
                );
                
                // Also update topic in selectedTopics array (since TopicCard reads from there)
                setSelectedTopics(prev => {
                    return prev.map(topic => {
                        // Match by either TopicId or ProviderTopicId (for student curriculum topics loaded via ProviderTopicId)
                        const topicIdToCheck = normalizeId(topic.e179_TopicId || topic.e180_TopicId);
                        const providerTopicIdToCheck = normalizeId(topic.e180_ProviderTopicId || topic.e179_TopicId);
                        const matches = topicIdToCheck === normalizedTopicId || providerTopicIdToCheck === normalizedTopicId;
                        
                        if (matches) {
                            // Check if this is a student curriculum topic (has e180_TopicId)
                            const isStudentCurriculumTopic = !!topic.e180_TopicId;
                            
                            // If it's a student curriculum topic, add details with e180_* field names
                            if (isStudentCurriculumTopic) {
                                const e180DetailsToAdd = {
                                    e180_PriorKnowledgeRequired: event.e272_PriorKnowledgeRequired || [],
                                    e180_NationalCurriculumContent: event.e272_NationalCurriculumContent || [],
                                    e180_Keywords: event.e272_Keywords?.map((k: any) => ({
                                        e180_Keyword: k.e272_Keyword,
                                        e180_Description: k.e272_Description
                                    })) || [],
                                    e180_LearningObjectives: event.e272_LearningObjectives || [],
                                    e180_SupervisionLevel: event.e272_SupervisionLevel,
                                    e180_Outcome: event.e272_Outcome,
                                    e180_TeacherTip: event.e272_TeacherTip,
                                    e180_Misconceptions: event.e272_Misconceptions,
                                    e180_DetailsLoaded: true
                                };
                                return { ...topic, ...detailsToAdd, ...e180DetailsToAdd };
                            } else {
                                // For provider topics, use e179_* fields
                                return { ...topic, ...detailsToAdd };
                            }
                        }
                        return topic;
                    });
                });
            }
        }
    });

    useEvent({
        eventName: 'E180v1_GotStudentCurriculum',
        onEventReceived: (event) => {
            if (event) {
                const e180Student = (event as { e180_StudentId?: string; E180_StudentId?: string }).e180_StudentId
                    ?? (event as { E180_StudentId?: string }).E180_StudentId;
                if (!shouldApplyCurriculumEventForStudent(e180Student, selectedStudentId)) {
                    return;
                }
                // Store E180 data - backend now returns complete topic data including newly added custom topics
                setStudentCurriculumData(event);
                
                const pendingExpand = pendingAddTopicsExpandPlacementsRef?.current;
                if (pendingExpand?.length) {
                    if (pendingAddTopicsExpandPlacementsRef) {
                        pendingAddTopicsExpandPlacementsRef.current = null;
                    }
                    setExpandedNodes(
                        computeStudentCurriculumExpandNodesForPlacements(event, pendingExpand)
                    );
                    setLoadedNodeIds(new Set());
                    setLoadingNodeIds(new Set());
                } else if (preservedExpansionRef.current) {
                    setExpandedNodes(preservedExpansionRef.current.expandedNodes);
                    setLoadedNodeIds(preservedExpansionRef.current.loadedNodeIds);
                    preservedExpansionRef.current = null;
                } else {
                    // Reset loaded nodes and expansion when student curriculum changes (normal reload).
                    // Otherwise nodes stay visually expanded (chevron down) but children are
                    // cleared, so user sees empty expansion until they collapse and re-expand.
                    setLoadedNodeIds(new Set());
                    setLoadingNodeIds(new Set());
                    setExpandedNodes(new Set());
                }
                
                // Always convert E180 structure to E179 structure for compatibility
                const convertedData = {
                    e179_Subjects: event.e180_Subjects?.map((subject: any) => ({
                        e179_Name: subject.e180_Name,
                        e179_SubjectId:
                            subject.e180_SubjectId != null && String(subject.e180_SubjectId).trim() !== ''
                                ? String(subject.e180_SubjectId)
                                : undefined,
                        e179_KeyStages: subject.e180_KeyStages?.map((keyStage: any) => ({
                            e179_Name: keyStage.e180_Name,
                            e179_KeyStageId: keyStage.e180_KeyStageId,
                            e179_Years: keyStage.e180_Years?.map((year: any) => ({
                                e179_Number: null,
                                e179_Name: year.e180_YearLabel,
                                e179_YearId: year.e180_YearId ?? year.E180_YearId,
                                e179_TotalTopicCount: year.e180_TotalTopicCount ?? year.E180_TotalTopicCount,
                                e179_Topics: (year.e180_Topics || []).map((topic: any) => convertTopic(topic))
                            }))
                        }))
                    }))
                };
                // Set curriculum data if we're viewing student curriculum
                // Use ref to get current value, but also check if we just switched to student curriculum
                // The ref should be updated by the time this event fires after setIsStudentCurriculumActive
                if (isStudentCurriculumActiveRef.current) {
                    setCurriculumData(convertedData);
                    setViewState(viewStates.curriculumReady);
                }
            }
        }
    });

    useEvent({
        eventName: 'E273v1_GotStudentCurriculumNodeChildren',
        onEventReceived: (event) => {
            if (event && event.e273_NodeId && event.e273_Topics) {
                // Remove from loading set
                setLoadingNodeIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(event.e273_NodeId);
                    return newSet;
                });
                
                // Add to loaded set - mark the parent node and all nested topic nodes as loaded
                setLoadedNodeIds(prev => {
                    const newSet = new Set(prev);
                    newSet.add(event.e273_NodeId);
                    return newSet;
                });
                
                // Update student curriculum data with loaded topics
                setStudentCurriculumData(prevData => {
                    if (!prevData || !prevData.e180_Subjects) return prevData;

                    const updatedData = JSON.parse(JSON.stringify(prevData)); // Deep clone
                    const target = parseStudentCurriculumLazyMergeTarget(
                        updatedData.e180_Subjects,
                        event.e273_NodeId,
                        event.e273_NodeType ?? ''
                    );

                    const mapTopicRecursive = (topic: any): any => ({
                        e180_Name: topic.e273_Name,
                        e180_TopicId: topic.e273_TopicId,
                        e180_Details: topic.e273_Details,
                        e180_MinHours: topic.e273_MinHours,
                        e180_PriorKnowledgeRequired: topic.e273_PriorKnowledgeRequired || [],
                        e180_NationalCurriculumContent: topic.e273_NationalCurriculumContent || [],
                        e180_Keywords: (topic.e273_Keywords || []).map((k: any) => ({
                            e180_Keyword: k.e273_Keyword,
                            e180_Description: k.e273_Description
                        })),
                        e180_LearningObjectives: topic.e273_LearningObjectives || [],
                        e180_SupervisionLevel: topic.e273_SupervisionLevel,
                        e180_Outcome: topic.e273_Outcome,
                        e180_TeacherTip: topic.e273_TeacherTip,
                        e180_Misconceptions: topic.e273_Misconceptions,
                        e180_ProviderKey: topic.e273_ProviderKey,
                        e180_ProviderTopicId: topic.e273_ProviderTopicId,
                        e180_State: topic.e273_State,
                        e180_ParentTopicNames: topic.e273_ParentTopicNames || [],
                        e180_TotalChildTopicCount: topic.e273_TotalChildTopicCount ?? 0,
                        e180_ChildTopics: (topic.e273_ChildTopics || []).map((childTopic: any) =>
                            mapTopicRecursive(childTopic)
                        )
                    });

                    if (target?.kind === 'year') {
                        const subject = target.subject;
                        if (
                            subject?.e180_KeyStages?.[target.keystageIndex]?.e180_Years?.[target.yearIndex]
                        ) {
                            subject.e180_KeyStages[target.keystageIndex].e180_Years[target.yearIndex].e180_Topics =
                                event.e273_Topics.map((topic: any) => mapTopicRecursive(topic));
                        }
                    } else if (target?.kind === 'topic') {
                        const subject = target.subject;
                        const year =
                            subject?.e180_KeyStages?.[target.keystageIndex]?.e180_Years?.[target.yearIndex];
                        if (year?.e180_Topics) {
                            let topic: any = year.e180_Topics[target.topicIndices[0]];
                            for (let i = 1; i < target.topicIndices.length && topic; i++) {
                                const idx = target.topicIndices[i];
                                if (topic.e180_ChildTopics && topic.e180_ChildTopics[idx] !== undefined) {
                                    topic = topic.e180_ChildTopics[idx];
                                } else {
                                    topic = null;
                                    break;
                                }
                            }
                            if (topic) {
                                topic.e180_ChildTopics = event.e273_Topics.map((childTopic: any) =>
                                    mapTopicRecursive(childTopic)
                                );
                            }
                        }
                    }

                    return updatedData;
                });
            }
        }
    });

    useEvent({
        eventName: 'E181v1_TopicsAddedToStudentCurriculum',
        onEventReceived: (event) => {
        if (addTopicsWizardBatchingRef?.current) {
            return;
        }
        setShowAddTopicsDialog(false);
        const tenantCustomCurriculumId =
            event?.e181_TenantCustomSubjectId ?? event?.E181_TenantCustomSubjectId;
        const myId = event?.e181_StudentCurriculumId ?? event?.E181_StudentCurriculumId;
        const studentCurriculumAdd = myId && String(myId).replace(/[{}]/g, '') !== '00000000-0000-0000-0000-000000000000';
        const tenantCustomCurriculumOnly = Boolean(tenantCustomCurriculumId) && !studentCurriculumAdd;
        const keepCreateYourOwnWizardOpen =
            createYourOwnWizardActiveRef?.current && tenantCustomCurriculumOnly;
        if (!keepCreateYourOwnWizardOpen) {
            setShowCreateYourOwnWizard(false);
        }
        setShowCreateCustomTopicsDialog(false);
        setShowAddCustomTopicDialog(false);
        setWizardInitialCatalogSubjectId('');
            // Collapse all expanded cards
            setExpandedCards(new Set());
            if (!tenantCustomCurriculumOnly) {
            // Switch back to student curriculum view
            setIsStudentCurriculumActive(true);
            // Update ref immediately to ensure it's set before reloading
            isStudentCurriculumActiveRef.current = true;
            }
            setSelectedTopics([]);
            setSelectedProvider('');
            
            // Preserve expansion state before reloading (will be restored in E180 handler)
            preservedExpansionRef.current = {
                expandedNodes: new Set(expandedNodes),
                loadedNodeIds: new Set(loadedNodeIds)
            };
            
            // After topics are added to a student (C259/C310): reload student curriculum. Tenant custom curriculum only (C262/C298/C316) skips this.
            if (selectedStudentId && !tenantCustomCurriculumOnly) {
                sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E299v1_CustomTopicFieldsUpdated',
        onEventReceived: (event) => {
            if (!event || !event.e299_TopicId) return;
            
            // Normalize topic ID for comparison
            const normalizeId = (id: any) => id?.toString().toLowerCase().trim();
            const normalizedTopicId = normalizeId(event.e299_TopicId || event.E299_TopicId);
            
            // Update the topic in selectedTopics array
            setSelectedTopics(prev => prev.map(topic => {
                const topicIdToCheck = normalizeId(topic.e179_TopicId || topic.e180_TopicId);
                if (topicIdToCheck === normalizedTopicId) {
                    // Create a new object with the updated fields
                    const updates: any = {};
                    if (event.e299_Details !== undefined || event.E299_Details !== undefined) {
                        updates.e180_Details = event.e299_Details || event.E299_Details || '';
                    }
                    if (event.e299_Outcome !== undefined || event.E299_Outcome !== undefined) {
                        updates.e180_Outcome = event.e299_Outcome || event.E299_Outcome || '';
                    }
                    if (event.e299_Misconceptions !== undefined || event.E299_Misconceptions !== undefined) {
                        updates.e180_Misconceptions = event.e299_Misconceptions || event.E299_Misconceptions || '';
                    }
                    if (event.e299_TeacherTip !== undefined || event.E299_TeacherTip !== undefined) {
                        updates.e180_TeacherTip = event.e299_TeacherTip || event.E299_TeacherTip || '';
                    }
                    if (event.e299_SupervisionLevel !== undefined || event.E299_SupervisionLevel !== undefined) {
                        updates.e180_SupervisionLevel = event.e299_SupervisionLevel || event.E299_SupervisionLevel || '';
                    }
                    if (event.e299_PriorKnowledgeRequired !== undefined || event.E299_PriorKnowledgeRequired !== undefined) {
                        updates.e180_PriorKnowledgeRequired = event.e299_PriorKnowledgeRequired || event.E299_PriorKnowledgeRequired || [];
                    }
                    if (event.e299_LearningObjectives !== undefined || event.E299_LearningObjectives !== undefined) {
                        updates.e180_LearningObjectives = event.e299_LearningObjectives || event.E299_LearningObjectives || [];
                    }
                    if (event.e299_Keywords !== undefined || event.E299_Keywords !== undefined) {
                        const keywords = event.e299_Keywords || event.E299_Keywords || [];
                        updates.e180_Keywords = keywords.map((k: any) => ({
                            e180_Keyword: k.e299_Keyword || k.E299_Keyword,
                            e180_Description: k.e299_Description || k.E299_Description
                        }));
                    }
                    
                    return { ...topic, ...updates, _updated: Date.now() };
                }
                return topic;
            }));
        }
    });

    useEvent({
        eventName: 'E274v1_GotReorderedTopics',
        onEventReceived: (event) => {
            if (event) {
                // Update student curriculum data with reordered topics
                // Map E274 structure to E180 structure
                const mappedData = {
                    e180_Subjects: event.e274_Subjects?.map((subject: any) => ({
                        e180_Name: subject.e274_Name,
                        e180_SubjectId: subject.e274_SubjectId,
                        e180_EducationSystemKey: subject.e274_EducationSystemKey ?? subject.E274_EducationSystemKey,
                        e180_EducationSystemDisplayName:
                            subject.e274_EducationSystemDisplayName ?? subject.E274_EducationSystemDisplayName,
                        e180_KeyStages: subject.e274_KeyStages?.map((keyStage: any) => ({
                            e180_Name: keyStage.e274_Name,
                            e180_KeyStageId: keyStage.e274_KeyStageId,
                            e180_Years: keyStage.e274_Years?.map((year: any) => ({
                                e180_YearId: year.e274_YearId ?? year.E274_YearId,
                                e180_YearLabel: year.e274_YearLabel,
                                e180_Topics: (year.e274_Topics || []).map((topic: any) => ({
                                    e180_Name: topic.e274_Name,
                                    e180_TopicId: topic.e274_TopicId,
                                    e180_Details: topic.e274_Details,
                                    e180_MinHours: topic.e274_MinHours,
                                    e180_PriorKnowledgeRequired: topic.e274_PriorKnowledgeRequired || [],
                                    e180_NationalCurriculumContent: topic.e274_NationalCurriculumContent || [],
                                    e180_Keywords: (topic.e274_Keywords || []).map((k: any) => ({
                                        e180_Keyword: k.e274_Keyword,
                                        e180_Description: k.e274_Description
                                    })),
                                    e180_LearningObjectives: topic.e274_LearningObjectives || [],
                                    e180_SupervisionLevel: topic.e274_SupervisionLevel,
                                    e180_Outcome: topic.e274_Outcome,
                                    e180_TeacherTip: topic.e274_TeacherTip,
                                    e180_Misconceptions: topic.e274_Misconceptions,
                                    e180_ProviderKey: topic.e274_ProviderKey,
                                    e180_ProviderTopicId: topic.e274_ProviderTopicId,
                                    e180_State: topic.e274_State,
                                    e180_ParentTopicNames: topic.e274_ParentTopicNames || [],
                                    e180_ChildTopics: (topic.e274_ChildTopics || []).map((childTopic: any) => ({
                                        e180_Name: childTopic.e274_Name,
                                        e180_TopicId: childTopic.e274_TopicId,
                                        e180_Details: childTopic.e274_Details,
                                        e180_MinHours: childTopic.e274_MinHours,
                                        e180_PriorKnowledgeRequired: childTopic.e274_PriorKnowledgeRequired || [],
                                        e180_NationalCurriculumContent: childTopic.e274_NationalCurriculumContent || [],
                                        e180_Keywords: (childTopic.e274_Keywords || []).map((k: any) => ({
                                            e180_Keyword: k.e274_Keyword,
                                            e180_Description: k.e274_Description
                                        })),
                                        e180_LearningObjectives: childTopic.e274_LearningObjectives || [],
                                        e180_SupervisionLevel: childTopic.e274_SupervisionLevel,
                                        e180_Outcome: childTopic.e274_Outcome,
                                        e180_TeacherTip: childTopic.e274_TeacherTip,
                                        e180_Misconceptions: childTopic.e274_Misconceptions,
                                        e180_ProviderKey: childTopic.e274_ProviderKey,
                                        e180_ProviderTopicId: childTopic.e274_ProviderTopicId,
                                        e180_State: childTopic.e274_State,
                                        e180_ParentTopicNames: childTopic.e274_ParentTopicNames || [],
                                        e180_ChildTopics: []
                                    }))
                                }))
                            }))
                        }))
                    }))
                };
                
                setStudentCurriculumData(mappedData);
                
                // Also update curriculumData for compatibility
                const convertedData = {
                    e179_Subjects: event.e274_Subjects?.map((subject: any) => ({
                        e179_Name: subject.e274_Name,
                        e179_SubjectId: subject.e274_SubjectId,
                        e179_KeyStages: subject.e274_KeyStages?.map((keyStage: any) => ({
                            e179_Name: keyStage.e274_Name,
                            e179_KeyStageId: keyStage.e274_KeyStageId,
                            e179_Years: keyStage.e274_Years?.map((year: any) => ({
                                e179_Number: year.e274_Number,
                                e179_Name: year.e274_Name ?? year.e274_YearLabel,
                                e179_YearId: year.e274_YearId ?? year.E274_YearId,
                                e179_Topics: (year.e274_Topics || []).map((topic: any) => convertTopic({
                                    e180_Name: topic.e274_Name,
                                    e180_TopicId: topic.e274_TopicId,
                                    e180_Details: topic.e274_Details,
                                    e180_MinHours: topic.e274_MinHours,
                                    e180_PriorKnowledgeRequired: topic.e274_PriorKnowledgeRequired,
                                    e180_NationalCurriculumContent: topic.e274_NationalCurriculumContent,
                                    e180_Keywords: topic.e274_Keywords,
                                    e180_LearningObjectives: topic.e274_LearningObjectives,
                                    e180_SupervisionLevel: topic.e274_SupervisionLevel,
                                    e180_Outcome: topic.e274_Outcome,
                                    e180_TeacherTip: topic.e274_TeacherTip,
                                    e180_Misconceptions: topic.e274_Misconceptions,
                                    e180_ProviderKey: topic.e274_ProviderKey,
                                    e180_ProviderTopicId: topic.e274_ProviderTopicId,
                                    e180_State: topic.e274_State,
                                    e180_ParentTopicNames: topic.e274_ParentTopicNames,
                                    e180_ChildTopics: topic.e274_ChildTopics
                                }))
                            }))
                        }))
                    }))
                };
                
                if (isStudentCurriculumActiveRef.current) {
                    setCurriculumData(convertedData);
                }
            }
        }
    });

    useEvent({
        eventName: 'E182v1_TopicsRemovedFromStudentCurriculum',
        onEventReceived: (event) => {
            // Get topic IDs from selected topics before clearing
            const topicIdsToRemove = selectedTopics
                .map(topic => topic.e180_TopicId)
                .filter((id): id is string => !!id);
            
            // Remove topics locally from both data structures without reloading
            // This preserves the tree expansion state
            if (topicIdsToRemove.length > 0) {
                removeTopicsFromData(
                    topicIdsToRemove,
                    curriculumData,
                    setCurriculumData,
                    studentCurriculumData,
                    setStudentCurriculumData
                );
            }
            
            // Clear selected topics
            setSelectedTopics([]);

            onTopicsRemovedFromStudentCurriculum?.();

            // Note: We don't reload student curriculum or reset loadedNodeIds/loadingNodeIds
            // This keeps the tree expanded so the user has to navigate back
        }
    });

    useEvent({
        eventName: 'E250v1_UnitCreated',
        onEventReceived: (event) => {
            setSelectedTopics([]);
            setSelectedLearningStyles([]);
            setHasAttemptedSubmit(false);
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
            // Navigate to review tab after unit creation
            window.location.hash = '#/app/lessons/review';
        }
    });

    useEvent({
        eventName: 'E258v1_LessonStarted',
        onEventReceived: (event: any) => {
            const ev = event as Record<string, unknown>;
            const unitWire = ev.e258_UnitId ?? ev.E258_UnitId;
            const lessonWire = ev.e258_LessonId ?? ev.E258_LessonId;
            const patch = buildPatchFromE258(ev);
            patchUnitsThenRefresh(prev =>
                applyLessonPatchToUnits(prev as any, unitWire, lessonWire, patch) as any
            );
            if (selectedStudentId) {
                sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E318v1_LessonSessionCompleted',
        onEventReceived: (event: any) => {
            const ev = event as Record<string, unknown>;
            const unitWire = ev.e318_UnitId ?? ev.E318_UnitId;
            const lessonWire = ev.e318_LessonId ?? ev.E318_LessonId;
            const patch = buildPatchFromE318(ev);
            patchUnitsThenRefresh(prev =>
                applyLessonPatchToUnits(prev as any, unitWire, lessonWire, patch, {
                    mergeInstructionSteps: false
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E326v1_LessonCustomisedAtDelivery',
        onEventReceived: (event: any) => {
            const ev = event as Record<string, unknown>;
            const unitWire = ev.e326_UnitId ?? ev.E326_UnitId;
            const lessonWire = ev.e326_LessonId ?? ev.E326_LessonId;
            const patch = buildPatchFromE326(ev);
            patchUnitsThenRefresh(prev =>
                applyLessonPatchToUnits(prev as any, unitWire, lessonWire, patch, {
                    mergeInstructionSteps: false
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E316v1_InstructionStepUpdated',
        onEventReceived: (event: any) => {
            const ev = event as Record<string, unknown>;
            const stepsRaw = ev.e316_InstructionSteps ?? ev.E316_InstructionSteps;
            if (stepsRaw === undefined) return;
            const steps = mapWireInstructionStepsFromEvent(stepsRaw);
            patchUnitsThenRefresh(prev =>
                applyLessonPatchToUnits(
                    prev as any,
                    ev.e316_UnitId ?? ev.E316_UnitId,
                    ev.e316_LessonId ?? ev.E316_LessonId,
                    { e251_InstructionSteps: steps }
                ) as any
            );
        }
    });

    useEvent({
        eventName: 'E269v1_UnitTouched',
        onEventReceived: (event: any) => {
            const unitId = event?.e269_UnitId ?? event?.E269_UnitId;
            if (unitId) {
                patchUnitsThenRefresh(prev => applyUnitTouchedToUnits(prev as any, unitId) as any);
            } else if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E166v1_AssessmentShared',
        onEventReceived: (event: any) => {
            const assessmentId = event?.e166_AssessmentId ?? event?.E166_AssessmentId;
            if (!assessmentId) return;
            patchUnitsThenRefresh(prev =>
                applyAssessmentSharedToUnits(prev as any, {
                    assessmentId,
                    sharedAssessmentId: event?.e166_SharedAssessmentId ?? event?.E166_SharedAssessmentId,
                    shareType: event?.e166_ShareTypeKey ?? event?.e166_ShareType ?? event?.E166_ShareType,
                    linkUrl: event?.e166_LinkUrl ?? event?.E166_LinkUrl
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E172v1_AssessmentStarted',
        onEventReceived: (event: any) => {
            patchUnitsThenRefresh(prev =>
                applyAssessmentStartedToUnits(prev as any, {
                    assessmentId: event?.e172_AssessmentId ?? event?.E172_AssessmentId,
                    unitId: event?.e172_UnitId ?? event?.E172_UnitId,
                    assessmentResultId: event?.e172_AssessmentResultId ?? event?.E172_AssessmentResultId
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E168v1_SharedAssessmentLinkRevoked',
        onEventReceived: (event: any) => {
            const sharedId = event?.e168_SharedAssessmentId ?? event?.E168_SharedAssessmentId;
            if (!sharedId) return;
            patchUnitsThenRefresh(prev =>
                applySharedAssessmentRevokedToUnits(prev as any, sharedId) as any
            );
        }
    });

    useEvent({
        eventName: 'E180v1_AssessmentApproved',
        onEventReceived: (event: any) => {
            patchUnitsThenRefresh(prev =>
                applyAssessmentApprovedToUnits(prev as any, {
                    assessmentId: event?.e180_AssessmentId ?? event?.E180_AssessmentId,
                    unitId: event?.e180_UnitId ?? event?.E180_UnitId,
                    assessmentResultId: event?.e180_AssessmentResultId ?? event?.E180_AssessmentResultId
                }) as any
            );
        }
    });

    // Event handlers for unit operations
    useEvent({
        eventName: 'E254v1_UnitApproved',
        onEventReceived: () => {
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
            window.location.hash = '#/app/lessons/inprogress';
        }
    });

    useEvent({
        eventName: 'E255v1_UnitDeleted',
        onEventReceived: () => {
            // Silently refresh without showing loading state
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
                // Refresh student curriculum to update topic states (topics set back to Ready)
                sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E315v1_LessonVideosApproved',
        onEventReceived: (event: any) => {
            const unitId = event?.e315_UnitId ?? event?.E315_UnitId;
            const lessonId = event?.e315_LessonId ?? event?.E315_LessonId;
            const materialId = event?.e315_MaterialId ?? event?.E315_MaterialId;
            if (!unitId || !lessonId || !materialId) return;
            patchUnitsThenRefresh(prev =>
                applyLessonVideoApprovedToUnits(prev as any, unitId, lessonId, materialId) as any
            );
        }
    });

    useEvent({
        eventName: 'E435v1_LessonRasterFigureReady',
        onEventReceived: (event: any) => {
            const unitId = event?.e435_UnitId ?? event?.E435_UnitId;
            const lessonId = event?.e435_LessonId ?? event?.E435_LessonId;
            const stepId = event?.e435_StepId ?? event?.E435_StepId;
            const figureKey = event?.e435_FigureKey ?? event?.E435_FigureKey;
            if (!unitId || !lessonId || !stepId || !figureKey) return;

            const generationState = event?.e435_GenerationState ?? event?.E435_GenerationState;
            // Status checks can reply still-generating; leave the spinner. Do not GetUnits — the
            // payload already has the figure fields the inline slot needs.
            if (String(generationState ?? '').trim().toLowerCase() === 'generating') return;

            const imageUrl = event?.e435_ImageUrl ?? event?.E435_ImageUrl;
            const rasterTitle = event?.e435_RasterTitle ?? event?.E435_RasterTitle;
            const generationError = event?.e435_GenerationError ?? event?.E435_GenerationError;
            const rasterApproved = event?.e435_RasterApproved ?? event?.E435_RasterApproved ?? false;
            const selectedOptionId = event?.e435_SelectedOptionId ?? event?.E435_SelectedOptionId;
            const imageGenerationPrompt =
                event?.e435_ImageGenerationPrompt ?? event?.E435_ImageGenerationPrompt;
            const rasterOptions = event?.e435_RasterOptions ?? event?.E435_RasterOptions;

            let didChange = false;
            setBackendLessons(prev => {
                const next = applyLessonRasterFigureReadyToUnits(
                    prev as any,
                    unitId,
                    lessonId,
                    stepId,
                    figureKey,
                    {
                        e251_GenerationState: generationState,
                        e251_ImageUrl: imageUrl,
                        e251_RasterTitle: rasterTitle,
                        e251_GenerationError: generationError,
                        e251_RasterApproved: rasterApproved,
                        e251_SelectedOptionId: selectedOptionId,
                        ...(typeof imageGenerationPrompt === 'string' && imageGenerationPrompt.trim()
                            ? { e251_ImageGenerationPrompt: imageGenerationPrompt }
                            : {}),
                        e251_RasterOptions: mapEventRasterOptions(rasterOptions),
                    }
                ) as any;
                didChange = next !== prev;
                return next;
            });
            if (didChange) {
                noteUnitsLocalMutation();
            }
        }
    });

    useEvent({
        eventName: 'E337v1_LessonRasterFigureApproved',
        onEventReceived: (event: any) => {
            const unitId = event?.e337_UnitId ?? event?.E337_UnitId;
            const lessonId = event?.e337_LessonId ?? event?.E337_LessonId;
            const materialId = event?.e337_MaterialId ?? event?.E337_MaterialId;
            const figureKey = event?.e337_FigureKey ?? event?.E337_FigureKey;
            if (!unitId || !lessonId || !materialId || !figureKey) return;
            patchUnitsThenRefresh(prev =>
                applyLessonRasterFigureApprovedToUnits(
                    prev as any,
                    unitId,
                    lessonId,
                    materialId,
                    figureKey
                ) as any
            );
        }
    });

    useEvent({
        eventName: 'E256v1_UnitRegenerated',
        onEventReceived: () => {
            // Silently refresh without showing loading state
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E334v1_LessonRegenerated',
        onEventReceived: () => {
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E439v1_LessonMaterialRegenerated',
        onEventReceived: () => {
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E330v1_LessonRemovedFromUnit',
        onEventReceived: (event: any) => {
            const unitId = event?.e330_UnitId ?? event?.E330_UnitId;
            const lessonId = event?.e330_LessonId ?? event?.E330_LessonId;
            if (!unitId || !lessonId) return;
            patchUnitsThenRefresh(prev =>
                applyLessonRemovedFromUnits(prev as any, unitId, lessonId) as any
            );
        }
    });

    useEvent({
        eventName: 'E263v1_UnitCompleted',
        onEventReceived: () => {
            // Refresh units when unit is completed
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
                // Refresh student curriculum to update topic states (topics set to Completed)
                sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
            }
            // Navigate to completed tab after unit completion
            window.location.hash = '#/app/lessons/completed';
        }
    });

    useEvent({
        eventName: 'E264v1_AssessmentCreated',
        onEventReceived: () => {
            // Refresh units when assessment is created
            if (selectedStudentId) {
                sendGetUnitsCommand({ C251_StudentId: selectedStudentId });
            }
        }
    });

    useEvent({
        eventName: 'E169v1_AssessmentCancelled',
        onEventReceived: (event: any) => {
            patchUnitsThenRefresh(prev =>
                applyAssessmentCancelledToUnits(prev as any, {
                    assessmentId: event?.e169_AssessmentId ?? event?.E169_AssessmentId,
                    unitId: event?.e169_UnitId ?? event?.E169_UnitId
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E174v1_AssessmentCompleted',
        onEventReceived: (event: any) => {
            const resultId = event?.e174_AssessmentResultId ?? event?.E174_AssessmentResultId;
            const unitId = event?.e174_UnitId ?? event?.E174_UnitId;
            const assessmentId = event?.e174_AssessmentId ?? event?.E174_AssessmentId;
            const studentId = event?.e174_StudentId ?? event?.E174_StudentId;

            patchUnitsThenRefresh(prev =>
                applyAssessmentCompletedToUnits(prev as any[], {
                    assessmentResultId: resultId,
                    unitId,
                    assessmentId,
                    studentId,
                    selectedStudentId
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E270v1_AssessmentResultsDeleted',
        onEventReceived: (event: any) => {
            const resultId = event?.e270_AssessmentResultId ?? event?.E270_AssessmentResultId;
            const unitId = event?.e270_UnitId ?? event?.E270_UnitId;
            const assessmentId = event?.e270_AssessmentId ?? event?.E270_AssessmentId;

            patchUnitsThenRefresh(prev =>
                applyAssessmentResultsDeletedToUnits(prev as any[], {
                    assessmentResultId: resultId,
                    unitId,
                    assessmentId
                }) as any
            );
        }
    });

    useEvent({
        eventName: 'E261v1_LessonFinished',
        onEventReceived: (event: any) => {
            const ev = event as Record<string, unknown>;
            const unitWire = ev.e261_UnitId ?? ev.E261_UnitId;
            const lessonWire = ev.e261_LessonId ?? ev.E261_LessonId;
            const patch = buildPatchFromE261(ev);
            patchUnitsThenRefresh(prev =>
                applyLessonPatchToUnits(prev as any, unitWire, lessonWire, patch) as any
            );
            if (selectedStudentId) {
                sendGetStudentCurriculumCommand({ C260_StudentId: selectedStudentId });
            }
        }
    });

}
